import { FC, useEffect, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { MyInput } from "../../../shared/ui/input";
import { Modal } from "../../../shared/ui/modal";
import classes from './sanctionSequenceReset.module.scss'

interface IProps {
    value?: number | null;
    canEdit: boolean;
    onUpdate: (value: number) => Promise<void>;
}

const normalizeValue = (value?: number | null): number => (
    typeof value === 'number' && Number.isFinite(value) ? value : 0
)

const validateValue = (value: string): string => {
    if(value.trim() === ''){
        return 'Укажите значение счётчика'
    }

    const parsedValue = Number(value)
    if(!Number.isFinite(parsedValue) || !Number.isInteger(parsedValue)){
        return 'Введите целое число'
    }

    if(parsedValue < 0){
        return 'Значение не может быть отрицательным'
    }

    return ''
}

export const SanctionSequenceReset: FC<IProps> = ({value, canEdit, onUpdate}) => {
    const [sanctionSequenceNumber, setSanctionSequenceNumber] = useState(() => normalizeValue(value))
    const [inputValue, setInputValue] = useState(() => String(normalizeValue(value)))
    const [inputError, setInputError] = useState('')
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isForbidden, setIsForbidden] = useState(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    useEffect(() => {
        const nextValue = normalizeValue(value)
        setSanctionSequenceNumber(nextValue)
        setInputValue(String(nextValue))
    }, [value])

    useEffect(() => {
        setIsForbidden(false)
    }, [canEdit])

    useEffect(() => {
        if(isEditOpen){
            setInputValue(String(sanctionSequenceNumber))
            setInputError('')
        }
    }, [isEditOpen, sanctionSequenceNumber])

    const save = async () => {
        const error = validateValue(inputValue)
        if(error){
            setInputError(error)
            return
        }

        const nextValue = Number(inputValue)
        try{
            setIsSaving(true)
            await onUpdate(nextValue)
            setSanctionSequenceNumber(nextValue)
            setIsEditOpen(false)
            setGlobalMessage({type: 'ok', message: 'Счётчик нарушений обновлён'})
        }
        catch(error){
            if(error instanceof AuthError){
                if(error.status === 401){
                    setIsAuth(false)
                }
                if(error.status === 403){
                    setIsForbidden(true)
                }
                setGlobalMessage({type: 'error', message: error.message})
            }
            else if(error instanceof MyError){
                setGlobalMessage({type: 'error', message: error.message})
            }
            else{
                setGlobalMessage({type: 'error', message: 'Не удалось обновить счётчик нарушений'})
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    const isEditAvailable = canEdit && !isForbidden

    if(sanctionSequenceNumber === 0 && !isEditAvailable){
        return null
    }

    return <>
        <section className={classes.container}>
            <span className={classes.title}>Счётчик нарушений</span>
            <strong className={classes.value}>{sanctionSequenceNumber}</strong>
            {isEditAvailable && (
                <button
                    type="button"
                    className={classes.editButton}
                    onClick={() => setIsEditOpen(true)}
                    disabled={isSaving}
                >
                    Редактировать
                </button>
            )}
        </section>
        <Modal open={isEditOpen} setOpen={setIsEditOpen}>
            <section className={classes.modalContent} role="dialog" aria-modal="true" aria-label="Редактировать счётчик нарушений">
                <h3 className={classes.modalTitle}>Редактировать счётчик нарушений</h3>
                <MyInput
                    title="Количество нарушений"
                    type="number"
                    min={0}
                    step={1}
                    value={inputValue}
                    setValue={setInputValue}
                    error={inputError}
                    setError={setInputError}
                    isLoading={isSaving}
                />
                <section className={classes.modalActions}>
                    <MyButtonSecondary onClick={() => setIsEditOpen(false)} disabled={isSaving}>Отмена</MyButtonSecondary>
                    <MyButton onClick={save} isLoading={isSaving}>Сохранить</MyButton>
                </section>
            </section>
        </Modal>
    </>
}
